// Hook pro čtení vlastních open a state parametrů aktuální agendy

import { useMemo } from 'react';
import { useNavigationStore } from './useNavigationContext';
import type { AgendaHandle } from '../core/agendaDefinition';
import type { ParamCodecMap, ParamOutput } from '../core/paramCodec';
import type { LayerPresentation } from '../core/navigationTypes';

interface AgendaParamsResult<TOpen extends ParamCodecMap, TState extends ParamCodecMap> {
  /** Dekódované open parametry */
  readonly open: ParamOutput<TOpen>;
  /** Dekódované state parametry */
  readonly state: ParamOutput<TState>;
  /** ID aktuální vrstvy */
  readonly layerId: string;
  /** Prezentace vrstvy */
  readonly presentation: LayerPresentation;
}

/**
 * Čte vlastní parametry agendy – stejný tvar pro root i modal režim.
 * Agenda nesmí znát svou hloubku ve stacku.
 */
export function useAgendaParams<TOpen extends ParamCodecMap, TState extends ParamCodecMap>(
  agenda: AgendaHandle<TOpen, TState>,
  layerId: string
): AgendaParamsResult<TOpen, TState> {
  const navigation = useNavigationStore((s) => s.navigation);

  return useMemo(() => {
    const layer = navigation.layers.find((l) => l.layerId === layerId && l.agendaId === agenda.id);

    if (!layer) {
      return {
        open: {} as ParamOutput<TOpen>,
        state: {} as ParamOutput<TState>,
        layerId,
        presentation: 'root' as const,
      };
    }

    return {
      open: layer.openParams as ParamOutput<TOpen>,
      state: layer.stateParams as ParamOutput<TState>,
      layerId: layer.layerId,
      presentation: layer.presentation,
    };
  }, [navigation, agenda.id, layerId]);
}
